/**
 * Fiche d'un candidat : TOUS ses résultats au même endroit.
 *
 * Remplace le couple « page parcours » (simple relais vers les épreuves) +
 * « page résultat d'une épreuve » : un onglet Vue d'ensemble (avancement,
 * scores de matching), puis un onglet par épreuve, chacun avec ses Résultats
 * bruts et, quand l'épreuve en porte un, son Matching.
 *
 * Partagée par les deux portails et rendue en pleine page ou en panneau depuis
 * la campagne. Présentationnelle pour tout ce qui dépend du périmètre :
 * l'en-tête (identité + actions), la lecture d'une épreuve et les passations de
 * la campagne arrivent du portail.
 */
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Progress,
  StatutEvaluationBadge,
} from "@repo/ui";
import { dateCourteHeure } from "@repo/utils";
import { ChevronRight } from "lucide-react";
import type { ReactNode } from "react";
import type { Id } from "../../../../convex/_generated/dataModel";
import { BoutonOnglet, ControleSegmente } from "../elementsNavigation";
import {
  cleEpreuve,
  descriptionEpreuve,
  epreuvesAffichees,
  libelleEpreuveFiche,
  matchingDeLEpreuve,
  useVueFiche,
  type EpreuveFiche,
  type VueEpreuve,
} from "./epreuvesFiche";
import {
  MatchingEpreuve,
  ScoreMatchingCandidat,
  type ContexteCampagneFiche,
} from "./MatchingFiche";

const TERMINE = "termine";

const OPTIONS_VUE: readonly { valeur: VueEpreuve; libelle: string }[] = [
  { valeur: "resultats", libelle: "Résultats" },
  { valeur: "matching", libelle: "Matching" },
];

type Props<E extends EpreuveFiche> = {
  parcoursId: Id<"parcoursEvaluations">;
  /** Identité du candidat et actions du portail (lien, archivage…). */
  entete: ReactNode;
  epreuves: readonly E[];
  progression: { pourcentage: number };
  /**
   * Campagne du candidat et ses passations, pour le matching. Absente = parcours
   * hors campagne : aucun matching, les épreuves n'ont que leurs Résultats.
   */
  campagne?: ContexteCampagneFiche;
  /** Résultats bruts d'une épreuve terminée, lus avec la query du portail. */
  renderResultats: (epreuve: E) => ReactNode;
  /** Actions propres au portail sur une épreuve (relancer une analyse…). */
  actionsEpreuve?: (epreuve: E) => ReactNode;
  /** Détail propre au portail sous une épreuve (erreurs d'analyse…). */
  detailEpreuve?: (epreuve: E) => ReactNode;
  /** Jeton d'auth et URL Convex de l'app : téléchargement des rapports. */
  token?: string | null;
  convexUrl?: string;
};

export function FicheCandidat<E extends EpreuveFiche>({
  parcoursId,
  entete,
  epreuves: toutesEpreuves,
  progression,
  campagne,
  renderResultats,
  actionsEpreuve,
  detailEpreuve,
  token,
  convexUrl,
}: Props<E>) {
  const vue = useVueFiche();
  const epreuves = epreuvesAffichees(toutesEpreuves);

  // L'URL peut désigner une épreuve que ce parcours n'a pas (lien d'un autre
  // candidat) : retour sur la vue d'ensemble.
  const epreuveActive = epreuves.find((e) => cleEpreuve(e) === vue.onglet);
  const genreActif = epreuveActive
    ? matchingDeLEpreuve(cleEpreuve(epreuveActive))
    : null;
  const avecMatching = genreActif !== null && campagne !== undefined;
  const vueEpreuve: VueEpreuve = avecMatching ? vue.vue : "resultats";

  return (
    <div className="space-y-6">
      {entete}

      <nav
        aria-label="Résultats du candidat"
        className="flex flex-wrap items-center gap-1 border-b"
      >
        <BoutonOnglet
          actif={!epreuveActive}
          onClick={() => vue.choisirOnglet("ensemble")}
        >
          Vue d'ensemble
        </BoutonOnglet>
        {epreuves.map((e) => (
          <BoutonOnglet
            key={e.evalId}
            actif={e === epreuveActive}
            onClick={() => vue.choisirOnglet(cleEpreuve(e))}
          >
            {libelleEpreuveFiche(e)}
          </BoutonOnglet>
        ))}
      </nav>

      {!epreuveActive ? (
        <VueEnsemble
          parcoursId={parcoursId}
          epreuves={epreuves}
          progression={progression}
          campagne={campagne}
          onOuvrir={(e, vueCible) => vue.ouvrir(cleEpreuve(e), vueCible)}
          actionsEpreuve={actionsEpreuve}
          detailEpreuve={detailEpreuve}
        />
      ) : epreuveActive.statut !== TERMINE ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-3 py-8 text-center text-sm text-muted-foreground">
            <StatutEvaluationBadge statut={epreuveActive.statut} detailAnalyse />
            <p>Les résultats seront disponibles une fois l'épreuve terminée.</p>
            {actionsEpreuve?.(epreuveActive)}
            {detailEpreuve?.(epreuveActive)}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {(avecMatching || actionsEpreuve) && (
            <div className="flex flex-wrap items-center justify-between gap-3">
              {avecMatching ? (
                <ControleSegmente
                  libelle="Lecture de l'épreuve"
                  options={OPTIONS_VUE}
                  valeur={vueEpreuve}
                  onChoisir={vue.choisirVue}
                />
              ) : (
                <span />
              )}
              {actionsEpreuve?.(epreuveActive)}
            </div>
          )}
          {vueEpreuve === "matching" && genreActif && campagne ? (
            <MatchingEpreuve
              genre={genreActif}
              parcoursId={parcoursId}
              campagne={campagne}
              token={token}
              convexUrl={convexUrl}
            />
          ) : (
            renderResultats(epreuveActive)
          )}
        </div>
      )}
    </div>
  );
}

function VueEnsemble<E extends EpreuveFiche>({
  parcoursId,
  epreuves,
  progression,
  campagne,
  onOuvrir,
  actionsEpreuve,
  detailEpreuve,
}: {
  parcoursId: Id<"parcoursEvaluations">;
  epreuves: E[];
  progression: { pourcentage: number };
  campagne?: ContexteCampagneFiche;
  onOuvrir: (epreuve: E, vue: VueEpreuve) => void;
  actionsEpreuve?: (epreuve: E) => ReactNode;
  detailEpreuve?: (epreuve: E) => ReactNode;
}) {
  const complet = progression.pourcentage === 100;
  return (
    <Card className="w-full">
      <CardHeader>
        <div className="flex items-center justify-between gap-4">
          <div>
            <CardTitle>Épreuves</CardTitle>
            <CardDescription>
              Avancement et matching de chaque épreuve du parcours
            </CardDescription>
          </div>
          <div className="flex min-w-[140px] items-center gap-2">
            <Progress
              value={progression.pourcentage}
              className={`h-2 flex-1 ${complet ? "[&>div]:bg-etat-fait" : ""}`}
            />
            <span
              className={`whitespace-nowrap text-sm font-medium ${complet ? "text-etat-fait" : ""}`}
            >
              {progression.pourcentage}%
            </span>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {epreuves.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">
            Aucune épreuve dans ce parcours.
          </p>
        ) : (
          <ul className="space-y-3">
            {epreuves.map((e) => {
              const genre = matchingDeLEpreuve(cleEpreuve(e));
              const termine = e.statut === TERMINE;
              const avecMatching = termine && genre !== null && campagne;
              return (
                <li
                  key={e.evalId}
                  className="rounded-surface border border-charte-line p-4 transition-colors duration-150 hover:border-charte-line-accent"
                >
                  <div className="flex flex-wrap items-center justify-between gap-4">
                    <div className="min-w-0">
                      <h3 className="font-medium text-encre">
                        {libelleEpreuveFiche(e)}
                      </h3>
                      <p className="text-sm text-ardoise">
                        {descriptionEpreuve(e)}
                      </p>
                      {e.dateDebut && (
                        <p className="mt-1 text-xs text-ardoise">
                          Commencé le {dateCourteHeure(e.dateDebut)}
                          {e.dateFin &&
                            ` · terminé le ${dateCourteHeure(e.dateFin)}`}
                        </p>
                      )}
                    </div>
                    <div className="flex flex-wrap items-center gap-4">
                      {avecMatching && (
                        <button
                          type="button"
                          onClick={() => onOuvrir(e, "matching")}
                          className="rounded-controle px-2 py-1 text-right transition-colors hover:bg-charte-hover"
                          aria-label={`Voir le matching ${libelleEpreuveFiche(e)}`}
                        >
                          <ScoreMatchingCandidat
                            genre={genre}
                            parcoursId={parcoursId}
                            campagne={campagne}
                          />
                        </button>
                      )}
                      <StatutEvaluationBadge statut={e.statut} detailAnalyse />
                      {actionsEpreuve?.(e)}
                      {termine && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => onOuvrir(e, "resultats")}
                        >
                          Voir les résultats
                          <ChevronRight className="ml-1 h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                  {detailEpreuve?.(e)}
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
